import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { memo } from 'react'
import {
  Users,
  MapPin,
  Heart,
  TrendingUp,
  Clock,
  Send,
} from 'lucide-react'
import { ContactCTA } from '@/components/sections'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import lavoraConNoiImg from '@/assets/images/sections/LavoraConNoi.jpg'

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  position: string
  message: string
}

// Benefits data
const benefits = [
  {
    id: 'growth',
    icon: TrendingUp,
    color: 'from-violet-400 to-purple-500',
  },
  {
    id: 'locations',
    icon: MapPin,
    color: 'from-purple-500 to-violet-600',
  },
  {
    id: 'flexibility',
    icon: Clock,
    color: 'from-violet-500 to-purple-600',
  },
  {
    id: 'team',
    icon: Heart,
    color: 'from-purple-600 to-violet-700',
  },
]

// Careers Badge
const CareersBadge = memo(function CareersBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-4 py-2 rounded-full shadow-lg"
    >
      <Users className="w-5 h-5" />
      <span className="font-semibold tracking-wide">BF CAREERS</span>
    </motion.div>
  )
})

// Benefits Card Component
const BenefitCard = memo(function BenefitCard({
  benefit,
  index,
  t,
}: {
  benefit: (typeof benefits)[0]
  index: number
  t: (key: string) => string
}) {
  const Icon = benefit.icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative group"
    >
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-white/80 backdrop-blur-sm border border-white/50 p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
        {/* Background gradient orb */}
        <div
          className={`absolute -top-10 -right-10 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br ${benefit.color} opacity-20 blur-2xl group-hover:opacity-30 transition-opacity`}
        />

        {/* Icon */}
        <div
          className={`relative w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-3 sm:mb-4 shadow-lg`}
        >
          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
        </div>

        {/* Content */}
        <h3 className="text-base sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
          {t(`benefits.${benefit.id}.title`)}
        </h3>
        <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
          {t(`benefits.${benefit.id}.description`)}
        </p>
      </div>
    </motion.div>
  )
})

export function WorkWithUs() {
  const { t, i18n } = useTranslation(['careers', 'forms', 'common'])
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>()

  const onSubmit = (data: FormData) => {
    console.log('Form submitted:', data)
    alert(t('careers:success'))
  }

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('careers:meta.title')}</title>
        <meta name="description" content={t('careers:meta.description')} />
      </Helmet>

      {/* Hero Section with Parallax Effect */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={lavoraConNoiImg}
            alt="Lavora con BF Wellness"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/60" />
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-64 h-64 rounded-full bg-white/5"
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                delay: i * 1.5,
              }}
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 20}%`,
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative container-custom py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <CareersBadge />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-6xl font-bold text-white mt-6 mb-6"
            >
              {t('careers:hero.title')}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl md:text-2xl text-white/90 leading-relaxed mb-8"
            >
              {t('careers:hero.subtitle')}
            </motion.p>

            {/* Keywords */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {(t('hero.tags', { returnObjects: true }) as string[]).map((word) => (
                <span
                  key={word}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold text-sm"
                >
                  {word}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 rounded-full bg-white/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 opacity-50 blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-purple-100 to-violet-100 opacity-50 blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('careers:why.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('careers:why.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={benefit.id}
                benefit={benefit}
                index={index}
                t={t as (key: string) => string}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="section-padding bg-white" id="candidatura">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('careers:form.title')}
              </h2>
              <p className="text-lg text-gray-600">{t('careers:form.subtitle')}</p>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              onSubmit={handleSubmit(onSubmit)}
              className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 md:p-10 border border-violet-100"
            >
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('forms:labels.firstName')} *
                  </label>
                  <Input
                    {...register('firstName', { required: t('forms:validation.nameRequired') })}
                    placeholder={t('forms:placeholders.firstName')}
                    className="bg-white"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('forms:labels.lastName')} *
                  </label>
                  <Input
                    {...register('lastName', { required: t('forms:validation.lastNameRequired') })}
                    placeholder={t('forms:placeholders.lastName')}
                    className="bg-white"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('forms:labels.email')} *
                  </label>
                  <Input
                    type="email"
                    {...register('email', {
                      required: t('forms:validation.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('forms:validation.emailInvalid'),
                      },
                    })}
                    placeholder={t('forms:placeholders.email')}
                    className="bg-white"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('forms:labels.phone')}
                  </label>
                  <Input
                    type="tel"
                    {...register('phone')}
                    placeholder={t('forms:placeholders.phone')}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('forms:labels.position')} *
                </label>
                <select
                  {...register('position', { required: t('forms:validation.positionRequired') })}
                  className="flex h-11 sm:h-12 w-full rounded-lg sm:rounded-xl border border-gray-200 bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 transition-all"
                >
                  <option value="">{t('forms:placeholders.position')}</option>
                  <option value={t('careers:positions.spaManager.title')}>{t('careers:positions.spaManager.title')}</option>
                  <option value={t('careers:positions.spaTherapist.title')}>{t('careers:positions.spaTherapist.title')}</option>
                  <option value={t('careers:positions.receptionist.title')}>{t('careers:positions.receptionist.title')}</option>
                  <option value={t('careers:positions.beautician.title')}>{t('careers:positions.beautician.title')}</option>
                  <option value={t('careers:positions.masseur.title')}>{t('careers:positions.masseur.title')}</option>
                  <option value={t('careers:positions.operator.title')}>{t('careers:positions.operator.title')}</option>
                  <option value={t('careers:positions.other')}>{t('careers:positions.other')}</option>
                </select>
                {errors.position && (
                  <p className="mt-1 text-sm text-red-500">{errors.position.message}</p>
                )}
              </div>

              <div className="mb-6 sm:mb-8">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('forms:labels.message')}
                </label>
                <Textarea
                  {...register('message')}
                  placeholder={t('forms:placeholders.experience')}
                  rows={4}
                  className="bg-white"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold py-3 sm:py-4 rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                {t('common:buttons.sendApplication')}
              </Button>
            </motion.form>
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  )
}
